import type { FormElement, FormStructure, TableElement } from '../model/form.types';
import type { FieldDefinition, FieldValue, MatrixAnswers } from '../model/types';
import { isEmptyValue } from './definitions/shared';
import { migrateLegacyElements } from './legacy-migrations';
import { getFieldDefinition } from './registry';
import type { RuleFieldSnapshot } from './rules';

export interface FieldEntry {
  element: FormElement;
  definition: FieldDefinition;
}

// The builder migrates these legacy shapes when it loads a matrix, so an old matrix must be read
// through the same migration here or it compiles differently from what its author saw.
// The list order is the form order: the builder renders it, and matrices saved before its drag
// renumbered `position` carry stale positions, so sorting by them would reorder the sheet.
export function migratedElements(structure: FormStructure): FormElement[] {
  return migrateLegacyElements(structure.elements);
}

export function isTableElement(element: FormElement): element is TableElement {
  return element.type === 'TABLE';
}

export interface PlacedElement {
  element: FormElement;
  enclosingTables: TableElement[];
}

export function* walkElements(elements: FormElement[], enclosingTables: TableElement[] = []): Generator<PlacedElement> {
  for (const element of elements) {
    if (element.type === 'HEADER') continue;
    yield { element, enclosingTables };
    if (!isTableElement(element)) continue;
    for (const row of element.properties.cells ?? []) {
      for (const cell of row) {
        if (cell.content) yield* walkElements([cell.content], [...enclosingTables, element]);
      }
    }
  }
}

export function* walkFields(elements: FormElement[]): Generator<FormElement> {
  for (const { element } of walkElements(elements)) {
    if (!isTableElement(element)) yield element;
  }
}

export function fieldEntries(elements: FormElement[]): FieldEntry[] {
  const entries: FieldEntry[] = [];
  for (const element of walkFields(elements)) {
    const definition = getFieldDefinition(element);
    if (definition) entries.push({ element, definition });
  }
  return entries;
}

// The engine keys answers, rules, permissions and table rows by element id, and its parts do not
// agree on which element a repeated id means, so a structure with one cannot be filled. Only fields
// and tables count: nothing is keyed by a header, a fixed text or an image, and a copy or a legacy
// migration can repeat their ids in a matrix that fills fine.
function repeatedElementSets(structure: FormStructure): FormElement[][] {
  const byId = new Map<string, FormElement[]>();
  for (const { element } of walkElements(migratedElements(structure))) {
    if (!isTableElement(element) && !getFieldDefinition(element)) continue;
    const group = byId.get(element.id);
    if (group) group.push(element);
    else byId.set(element.id, [element]);
  }
  return [...byId.values()].filter((group) => group.length > 1);
}

export function repeatedElementIds(structure: FormStructure): string[] {
  return repeatedElementSets(structure).map(([element]) => element.id);
}

// The id never shows in the builder, so the person can only find the elements by their labels.
// The structure is untyped JSON from the database, so a label that is not text is ignored.
function elementLabel(element: FormElement): string {
  const { label, name } = (element.properties ?? {}) as { label?: unknown; name?: unknown };
  if (typeof label === 'string' && label.trim() !== '') return label.trim();
  if (typeof name === 'string' && name.trim() !== '') return name.trim();
  return isTableElement(element) ? 'Tabela' : 'Campo sem rótulo';
}

export function elementLabelsById(structure: FormStructure, ids: string[]): string[] {
  const elements = new Map<string, FormElement>();
  for (const { element } of walkElements(migratedElements(structure))) {
    if (!elements.has(element.id)) elements.set(element.id, element);
  }
  return ids.map((id) => {
    const element = elements.get(id);
    return element ? elementLabel(element) : 'Campo sem rótulo';
  });
}

export function repeatedElementLabels(structure: FormStructure): string[][] {
  return repeatedElementSets(structure).map((group) => group.map(elementLabel));
}

const NAMED_GROUPS_LIMIT = 3;

// A copied table repeats one id per cell, so the list stops after a few groups to fit a toast.
function namedGroups(labelGroups: string[][]): string {
  const named = labelGroups.slice(0, NAMED_GROUPS_LIMIT).map((labels) => labels.join(' e '));
  const rest = labelGroups.length - NAMED_GROUPS_LIMIT;
  return rest > 0 ? `${named.join('; ')}; e mais ${rest}` : named.join('; ');
}

export function repeatedElementsMessage(labelGroups: string[][]): string {
  return `A estrutura desta matriz tem campos com o mesmo identificador interno (${namedGroups(labelGroups)}) e não pode ser preenchida. Avise o responsável pela matriz.`;
}

export function ruleCycleMessage(labelGroups: string[][]): string {
  return `As regras desta matriz formam um ciclo (${namedGroups(labelGroups)}) e ela não pode ser preenchida. Avise o responsável pela matriz.`;
}

// The builder never shows the id, so the person fixes a repeated one by inserting the field again.
export function repeatedElementsSaveMessage(labelGroups: string[][]): string {
  return `A estrutura tem campos com o mesmo identificador interno (${namedGroups(labelGroups)}). Remova e insira de novo um dos campos de cada grupo antes de salvar.`;
}

export function ruleCycleSaveMessage(labelGroups: string[][]): string {
  return `As regras formam um ciclo (${namedGroups(labelGroups)}). Desfaça o ciclo antes de salvar.`;
}

// Never compile here: compiling runs the author's regex on the builder default value, unbounded.
export function listFieldIds(structure: FormStructure): string[] {
  return [...new Set(fieldEntries(migratedElements(structure)).map(({ element }) => element.id))];
}

export function hasStoredValue(values: Record<string, FieldValue>, fieldId: string): boolean {
  return Object.prototype.hasOwnProperty.call(values, fieldId);
}

export function sameFieldValue(left: FieldValue | undefined, right: FieldValue | undefined): boolean {
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((item, index) => item === right[index]);
  }
  return left === right;
}

// An empty stored value does not freeze: a later responder fills what nobody answered yet, and
// only an answered field is locked from the next response on (product rule). Empty is the same
// test validate uses, so a whitespace-only text that passed as blank never locks the field.
export function listAnsweredFieldIds(structure: FormStructure, answers: MatrixAnswers): string[] {
  return listFieldIds(structure).filter(
    (fieldId) => hasStoredValue(answers.values, fieldId) && !isEmptyValue(answers.values[fieldId]),
  );
}

// Both empty is the same answer, whatever the empty form ('', [], null or no key): typing into a
// field and clearing it again writes nothing.
export function sameAnswer(left: FieldValue | undefined, right: FieldValue | undefined): boolean {
  if (isEmptyValue(left ?? null) && isEmptyValue(right ?? null)) return true;
  return sameFieldValue(left, right);
}

// What a later response writes: the values it changed from the ones it opened with.
export function listWrittenFieldIds(opened: MatrixAnswers, current: MatrixAnswers): string[] {
  return Object.keys(current.values).filter((fieldId) => !sameAnswer(opened.values[fieldId], current.values[fieldId]));
}

export function currentValue(element: FormElement, answers: MatrixAnswers): FieldValue {
  const definition = getFieldDefinition(element);
  if (!definition) return null;
  const stored = answers.values[element.id];
  return stored === undefined ? definition.emptyValue(element) : stored;
}

export function snapshotFields(elements: FormElement[], answers: MatrixAnswers): Record<string, RuleFieldSnapshot> {
  const snapshot: Record<string, RuleFieldSnapshot> = {};
  for (const { element } of fieldEntries(elements)) {
    snapshot[element.id] = { element, value: currentValue(element, answers) };
  }
  return snapshot;
}
